/**
 * Shared feature-attribute formatting used by BOTH the GeoServer WMS and the
 * Esri REST query paths.
 *
 * Any formatting bug is fixed here once, rather than in each source's handler —
 * this is what Bug 2 was caused by: the Esri path dumped raw field names
 * straight into popup HTML without going through a shared formatter.
 */

export type FeatureSourceType = 'geoserver' | 'esri';

export interface FormattedField {
  label: string;
  value: string;
}

export interface FeatureInfoResult {
  /** Stable key for list rendering. */
  id: string;
  /** Human-readable layer name shown as the popup heading. */
  layerTitle: string;
  sourceType: FeatureSourceType;
  fields: FormattedField[];
}

/** Field names that carry no meaning for an end user. */
const HIDDEN_FIELDS = new Set([
  'shape',
  'shape_length',
  'shape_area',
  'st_length(shape)',
  'st_area(shape)',
  'geometry',
  'geom',
  'the_geom',
  'bounding_box',
]);

/** Common GIS abbreviations that title-casing alone would mangle. */
const ABBREVIATION_LABELS: Record<string, string> = {
  objectid: 'Object ID',
  fid: 'Feature ID',
  gid: 'ID',
  id: 'ID',
  numoccup: 'Number of Occupants',
  deduct: 'Deductible',
  predisval: 'Pre-Disaster Value',
  strloss: 'Structure Loss',
  contloss: 'Contents Loss',
  strinsur: 'Structure Insured',
  continsur: 'Contents Insured',
  incidentid: 'Incident ID',
  incidentnm: 'Incident Name',
  inspectid: 'Inspection ID',
  inspdate: 'Inspection Date',
  inspemail: 'Inspector Email',
  inspphone: 'Inspector Phone',
  fulladdr: 'Full Address',
  pstlcity: 'City',
  pstlzip5: 'Postal Code',
  parcelid: 'Parcel ID',
  usngcoord: 'USNG Coordinate',
  primres: 'Primary Residence',
  habitable: 'Habitable',
};

/** True for columns that carry no meaning for an end user (geometry, shape length…). */
export function isHiddenField(fieldName: string): boolean {
  return HIDDEN_FIELDS.has(fieldName.trim().toLowerCase());
}

/**
 * Turns a raw field name into a readable label.
 * `numoccup` → "Number of Occupants", `contact_first_name` → "Contact First Name".
 */
export function humanizeFieldName(fieldName: string): string {
  const key = fieldName.trim().toLowerCase();
  if (ABBREVIATION_LABELS[key]) return ABBREVIATION_LABELS[key];

  return fieldName
    .replace(/[_-]+/g, ' ')
    // split camelCase / PascalCase
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (character) => character.toUpperCase());
}

/** Heuristic: ArcGIS returns dates as epoch milliseconds in plain number fields. */
function looksLikeEpochMillis(fieldName: string, value: number): boolean {
  return /date|time|created|updated|inspd/i.test(fieldName) && value > 10_000_000_000;
}

/**
 * Formats a single attribute value for display.
 * Returns null when the value should be skipped entirely.
 */
export function formatFieldValue(fieldName: string, value: unknown): string | null {
  if (value === null || value === undefined) return null;

  // GeoServer/ArcGIS both use these sentinels for "no data".
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed === '' || trimmed.toLowerCase() === 'null') return null;
    return trimmed;
  }

  if (typeof value === 'number') {
    if (Number.isNaN(value)) return null;
    if (looksLikeEpochMillis(fieldName, value)) {
      const date = new Date(value);
      if (!Number.isNaN(date.getTime())) return date.toLocaleString();
    }
    return String(value);
  }

  if (typeof value === 'boolean') return value ? 'Yes' : 'No';

  // Nested objects (e.g. geometry) are never useful in a popup.
  return null;
}

/**
 * THE shared formatter. Both source paths call this.
 *
 * @param attributes  raw key/value bag from GeoJSON `properties` or Esri `attributes`
 * @param aliases     optional field-name → alias map (Esri `fields[].alias`)
 */
/**
 * Metadata fields that belong in the popup FOOTER rather than the attribute
 * table. Keys are matched case-insensitively against the raw field names.
 * Covers ArcGIS editor-tracking defaults plus common GeoServer conventions.
 */
const METADATA_MATCHERS: { label: string; names: string[] }[] = [
  { label: 'Object ID', names: ['objectid', 'object_id', 'fid', 'oid', 'gid', 'feature_id'] },
  { label: 'Created On', names: ['created_date', 'creationdate', 'created_on', 'created', 'createdate'] },
  { label: 'Created By', names: ['creator', 'created_user', 'created_by', 'createdby'] },
  {
    label: 'Last Updated',
    names: ['last_edited_date', 'editdate', 'last_updated', 'modified_date', 'last_edit_date'],
  },
  {
    label: 'Last Updated By',
    names: ['last_edited_user', 'editor', 'last_updated_by', 'modified_by', 'updated_by'],
  },
];

export interface MetadataEntry {
  label: string;
  value: string;
}

/**
 * Splits raw attributes into footer metadata and the remaining table fields.
 * Only metadata that actually exists (and is non-empty) is returned, so the
 * footer never renders placeholder columns.
 */
export function extractMetadata(attributes: Record<string, unknown>): {
  metadata: MetadataEntry[];
  remaining: Record<string, unknown>;
} {
  const remaining: Record<string, unknown> = { ...attributes };
  const metadata: MetadataEntry[] = [];

  for (const { label, names } of METADATA_MATCHERS) {
    const matchKey = Object.keys(remaining).find((key) =>
      names.includes(key.trim().toLowerCase().replace(/\s+/g, '_')),
    );
    if (!matchKey) continue;

    const value = formatFieldValue(matchKey, remaining[matchKey]);
    if (value !== null) metadata.push({ label, value });
    // Remove it either way so it never double-renders in the table.
    delete remaining[matchKey];
  }

  return { metadata, remaining };
}

export function formatFeatureForPopup(
  attributes: Record<string, unknown>,
  options: {
    id: string;
    layerTitle: string;
    sourceType: FeatureSourceType;
    aliases?: Record<string, string>;
  },
): FeatureInfoResult {
  const fields: FormattedField[] = [];

  for (const [fieldName, rawValue] of Object.entries(attributes ?? {})) {
    if (HIDDEN_FIELDS.has(fieldName.trim().toLowerCase())) continue;

    const value = formatFieldValue(fieldName, rawValue);
    if (value === null) continue; // skip null/empty

    // Prefer the service-provided alias over our own humanising.
    const alias = options.aliases?.[fieldName];
    fields.push({
      label: alias && alias.trim() ? alias.trim() : humanizeFieldName(fieldName),
      value,
    });
  }

  return {
    id: options.id,
    layerTitle: options.layerTitle,
    sourceType: options.sourceType,
    fields,
  };
}
